import {useCallback, useMemo} from 'react';

import * as CustomRunColorsActions from '../../../state/views/customRunColors/actions';
import type * as CustomRunColorsTypes from '../../../state/views/customRunColors/types';
import * as ViewHooks from '../../../state/views/hooks';
import {type Ref} from '../../../state/views/runSet/types';
import type * as SemanticLegendTypes from '../../../state/views/semanticLegend/types';
import {resolveRunColorState, type RunColorSource} from '../../../util/colors';
import {getFiltersForQuery} from '../../../util/filterV2';
import {
  getParsedRunColorGroupKeys,
  getRunColorGroupDescendantKeysFromParsedKeys,
  getRunColorGroupKeyForDepth,
} from '../../../util/runColorGroup';
import * as Run from '../../../util/runs';
import {type Key} from '../../../util/runTypes';
import {type WBTableRowWithRunSetInfo} from '../../../util/tablecols';
import {useWorkspaceSettingsColorPalette} from '../../WorkspaceDrawer/Settings/colorPalette/ColorPalette';
import {useSemanticLegend} from '../../WorkspaceDrawer/Settings/semanticLegend/useSemanticLegend';

type UseRunColorResult = {
  clearCustomRunColor: () => void;
  customRunColor: string;
  customRunColorPreviewColors: string[];
  customRunColorSource: RunColorSource;
  hasCustomRunColorDescendantColors: boolean;
  hasParentInheritedColor: boolean;
  isCustomRunColorClearable: boolean;
  setCustomRunColor: (color: string) => void;
};

type UseRunColorProps = {
  customRunColorsRef: CustomRunColorsTypes.Ref;
  entityName: string;
  grouping: Key[];
  isGroup?: boolean;
  recursionDepth: number;
  projectName: string;
  row: WBTableRowWithRunSetInfo;
  runSetRef: Ref;
  semanticLegendRef: SemanticLegendTypes.Ref;
};
export function useRunColor({
  customRunColorsRef,
  entityName,
  grouping,
  isGroup,
  recursionDepth,
  projectName,
  row,
  runSetRef,
  semanticLegendRef,
}: UseRunColorProps): UseRunColorResult {
  const groupKeys = useMemo(
    () => (isGroup ? grouping.slice(0, recursionDepth + 1) : grouping),
    [grouping, isGroup, recursionDepth]
  );
  const runColorKey = Run.uniqueId(row, groupKeys);
  const groupColorKey = useMemo(
    () =>
      isGroup && groupKeys.length > 0
        ? getRunColorGroupKeyForDepth(row, grouping, recursionDepth)
        : null,
    [groupKeys.length, grouping, isGroup, recursionDepth, row]
  );

  const runSet = ViewHooks.usePartMaybe(runSetRef);
  const storedFilters = ViewHooks.usePartMaybe(runSet?.filtersRef);
  const filters = useMemo(
    () => (storedFilters ? getFiltersForQuery(storedFilters) : undefined),
    [storedFilters]
  );
  const sort = ViewHooks.usePartMaybe(runSet?.sortRef);
  const semanticLegendFromRef = ViewHooks.usePartMaybe(semanticLegendRef);
  const semanticLegendSettings = useSemanticLegend(
    entityName,
    projectName,
    filters,
    sort,
    undefined,
    runSet?.search,
    semanticLegendFromRef
  );
  const colorPaletteOption = useWorkspaceSettingsColorPalette();
  const customRunColors = ViewHooks.usePartMaybe(customRunColorsRef);
  const customRunColorState = resolveRunColorState({
    run: row,
    groupKeys,
    customRunColors,
    semanticLegendSettings,
    colorPaletteOption,
  });
  const parsedRunColorGroupKeys = useMemo(
    () => getParsedRunColorGroupKeys(customRunColors),
    [customRunColors]
  );
  const customRunColorDescendantKeys = useMemo(
    () =>
      groupColorKey == null
        ? []
        : getRunColorGroupDescendantKeysFromParsedKeys(
            parsedRunColorGroupKeys,
            groupColorKey
          ),
    [groupColorKey, parsedRunColorGroupKeys]
  );

  // used for color picker preview
  const customRunColorPreview: {
    colors: string[];
    hasDescendantColors: boolean;
  } = useMemo(() => {
    if (groupColorKey == null || customRunColors == null) {
      return {
        colors: [customRunColorState.color],
        hasDescendantColors: false,
      };
    }

    // grouping, check for multiple colors in the group
    const descendantColors = customRunColorDescendantKeys
      .map(key => customRunColors[key])
      .filter((color): color is string => color != null);

    if (descendantColors.length === 0) {
      return {
        colors: [customRunColorState.color],
        hasDescendantColors: false,
      };
    }

    const colors =
      customRunColorState.source === 'automatic'
        ? descendantColors
        : [customRunColorState.color, ...descendantColors];

    return {
      colors: Array.from(new Set(colors)).slice(0, 3),
      hasDescendantColors: true,
    };
  }, [
    customRunColorState.color,
    customRunColorState.source,
    customRunColors,
    groupColorKey,
    customRunColorDescendantKeys,
  ]);

  const setCustomRunColor = ViewHooks.useViewAction(
    customRunColorsRef,
    CustomRunColorsActions.setCustomRunColor
  );
  const setCustomRunColors = ViewHooks.useViewAction(
    customRunColorsRef,
    CustomRunColorsActions.setCustomRunColors
  );

  const clearCustomRunColorUpdates = useMemo(() => {
    const colorUpdates: Record<string, null> = {};

    if (groupColorKey != null && customRunColors?.[groupColorKey] != null) {
      colorUpdates[groupColorKey] = null;
    }

    if (customRunColors?.[runColorKey] != null) {
      colorUpdates[runColorKey] = null;
    }

    return colorUpdates;
  }, [runColorKey, customRunColors, groupColorKey]);

  const isCustomRunColorClearable =
    Object.keys(clearCustomRunColorUpdates).length > 0;
  const hasParentInheritedColor =
    customRunColorState.parentInheritedColor != null;

  const setCustomRunColorForRow = useCallback(
    (color: string) => {
      if (groupColorKey != null) {
        const colorUpdates: Record<string, string | null> = {
          [groupColorKey]: color,
        };
        // if group, clear all children colors
        if (customRunColors?.[runColorKey] != null) {
          colorUpdates[runColorKey] = null;
        }
        customRunColorDescendantKeys.forEach(key => {
          colorUpdates[key] = null;
        });

        setCustomRunColors(colorUpdates);
        return;
      }

      setCustomRunColor(runColorKey, color);
    },
    [
      runColorKey,
      customRunColors,
      groupColorKey,
      customRunColorDescendantKeys,
      setCustomRunColor,
      setCustomRunColors,
    ]
  );
  const clearCustomRunColorForRow = useCallback(() => {
    if (!isCustomRunColorClearable) {
      return;
    }

    setCustomRunColors(clearCustomRunColorUpdates);
  }, [
    clearCustomRunColorUpdates,
    isCustomRunColorClearable,
    setCustomRunColors,
  ]);

  return {
    clearCustomRunColor: clearCustomRunColorForRow,
    customRunColor: customRunColorState.color,
    customRunColorPreviewColors: customRunColorPreview.colors,
    customRunColorSource: customRunColorState.source,
    hasCustomRunColorDescendantColors:
      customRunColorPreview.hasDescendantColors,
    hasParentInheritedColor,
    isCustomRunColorClearable,
    setCustomRunColor: setCustomRunColorForRow,
  };
}
